/**
 * BrowsePage Component
 *
 * @remarks
 * Fetches and renders a complete browse page with all rails and tiles.
 * Uses the fetchBrowsePage utility to orchestrate the data fetching flow.
 */

import { useEffect, useState } from "react";
import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";

import { fetchBrowsePage } from "@/utils/FetchBrowsePage";
import { RailWithTiles } from "@/utils/FetchBrowsePage/types";
import { Rail } from "@/components/organisms/Rail";
import { Spotlight } from "@/components/organisms/Spotlight";
import { Tile34 } from "@/components/molecules/Tile34";
import { AssetResponse } from "@/utils/FetchAssetDetails/types";
import { PeacockNodeType, TemplateType } from "@/utils/shared-atom-types";
import { MiniGameTile } from "@/components/molecules/MiniGameTile";
import { SecondaryNavTile } from "@/components/molecules/SecondaryNavTile";
import { getTileFormat } from "@/utils/GetTileFormat";

import { BrowsePageProps } from "./types";
import styles from "./BrowsePage.module.scss";

const BrowsePage = ({
  slug,
  fetchTileDetails = true,
  skipPersonalized = true,
  showMobileContent = true,
  scrollContainerRef,
  className,
  ...props
}: BrowsePageProps) => {
  const [rails, setRails] = useState<RailWithTiles[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const browsePageRails = await fetchBrowsePage(slug, {
          fetchTileDetails,
          skipPersonalized,
          showMobileContent,
        });

        setRails(browsePageRails);
      } catch (err) {
        /** Silently fail - errors are expected for restricted content */
        setError(
          err instanceof Error ? err.message : "Failed to fetch browse page",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, fetchTileDetails, skipPersonalized, showMobileContent]);

  const cn = classNames(styles["browse-page"], className);

  if (loading) {
    return (
      <div className={cn} {...props}>
        Loading browse page...
      </div>
    );
  }

  if (!rails || rails.length === 0) {
    return (
      <div className={cn} {...props}>
        <div className={styles["browse-page__loading"]}>No rails found</div>
      </div>
    );
  }

  return (
    <div className={cn} {...props}>
      {rails.map((rail) => {
        /** Skip rails that were marked as skipped (e.g., personalized rails) */
        if (rail.skipped) {
          return null;
        }

        /** Skip rails with errors */
        if (rail.error) {
          console.warn(
            `Skipping rail "${rail.title}" due to error:`,
            rail.error,
          );
          return null;
        }

        /** For SHOWCASE template rails, use original tiles to preserve correct artwork */
        /** For other rails, use tilesWithDetails if available, otherwise fall back to tiles */
        const isShowcase = rail.renderHint?.template === TemplateType.Showcase;
        let tilesToRender = rail.tiles;

        /** Filter out LINEAR_SLOT tiles */
        tilesToRender = tilesToRender.filter((tile: AssetResponse) => {
          if (tile.type === PeacockNodeType.LinearSlot) {
            console.info(
              `🚫 Skipping LINEAR_SLOT tile: "${tile.attributes?.title || tile.id}"`,
            );
            return false;
          }
          return true;
        });

        /** Skip empty rails (after filtering) */
        if (!tilesToRender || tilesToRender.length === 0) {
          return null;
        }

        /** Render Spotlight for SHOWCASE template with a single asset */
        if (isShowcase && tilesToRender.length === 1) {
          const spotlightAsset = tilesToRender[0];

          return (
            <Spotlight
              key={rail.id}
              asset={spotlightAsset}
              title={rail.title}
              scrollContainerRef={scrollContainerRef}
            />
          );
        }

        /** Render standard Rail for all other cases */
        return (
          <Rail title={rail.title} key={rail.id}>
            {tilesToRender.map((tile: AssetResponse, index: number) => {
              /** Determine the tile format based on asset type and rail render hints */
              if (rail.title.includes("Brand")) {
                return (
                  <SecondaryNavTile
                    key={tile.id || `tile-${index}`}
                    asset={tile}
                  />
                );
              }

              const format = getTileFormat(tile, rail.renderHint);

              if (rail.renderHint.template === TemplateType.Gamerail) {
                return (
                  <MiniGameTile key={tile.id || `tile-${index}`} asset={tile} />
                );
              }

              return (
                <Tile34
                  key={tile.id || `tile-${index}`}
                  asset={tile}
                  format={format}
                />
              );
            })}
          </Rail>
        );
      })}
    </div>
  );
};

export { BrowsePage };
